import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Mail, MessageCircle, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Marquee from "./Marquee";
import { resolveBusinessPhone } from '../lib/contactDetails';

const WA_PHONE = "18196490166";

export const Footer = () => {
  const { t } = useI18n();
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);
  const phone = resolveBusinessPhone(profile?.phone);
  const phoneRaw = phone.replace(/\D/g, "");
  const email = profile?.email || "info@locationrnc.ca";
  const address = profile?.address || "Région du Nord, Québec";
  const hours = profile?.hours || "Lun – Sam : 7h – 18h\nDim : sur rendez-vous";
  const contactName = profile?.contact_name || "Phil";

  const waHref = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(t("wa.message"))}`;

  return (
    <footer className="bg-[#0B1620] text-[#F5F1EA] relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-blueprint-dense opacity-40 pointer-events-none" />

      {/* HUGE editorial marquee inside footer */}
      <div className="relative border-b border-white/10 py-6">
        <Marquee
          items={["Location RNC", "Québec — Nord", "Remorques", "Outillage", "Rénovation"]}
          className="marquee-mask"
          speed={55}
          itemClassName="text-white/12 text-stroke"
          separator={<span className="mx-6 text-[#E87B3A]">•</span>}
        />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12 py-20 grid grid-cols-12 gap-10">
        {/* BRAND */}
        <div className="col-span-12 md:col-span-5">
          <Logo size={56} withText inverted />
          <p className="mt-6 font-body text-white/70 max-w-md leading-relaxed">
            {t("footer.tagline")}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${phoneRaw}`}
              data-testid="footer-cta-call"
              className="group inline-flex items-center gap-3 bg-[#E87B3A] hover:bg-[#F08A47] text-white px-5 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em]"
            >
              <Phone size={14} /> {phone}
              <ArrowUpRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href={waHref}
              target="_blank" rel="noopener noreferrer"
              data-testid="footer-cta-wa"
              className="group inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1EBE5B] text-white px-5 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em]"
            >
              <MessageCircle size={14} /> {t("footer.whatsapp")}
            </a>
          </div>
        </div>

        {/* NAV */}
        <div className="col-span-6 md:col-span-3">
          <div className="chapter-label text-[#E87B3A]">{t("footer.nav")}</div>
          <ul className="mt-6 space-y-3 font-display uppercase text-2xl leading-tight">
            <li><Link to="/" className="hover:text-[#E87B3A] transition-colors" data-testid="footer-home">{t("nav.home")}</Link></li>
            <li><Link to="/catalogue" className="hover:text-[#E87B3A] transition-colors" data-testid="footer-catalog">{t("nav.catalog")}</Link></li>
            <li><Link to="/contact" className="hover:text-[#E87B3A] transition-colors" data-testid="footer-contact">{t("nav.contact")}</Link></li>
            <li><Link to="/admin/login" className="hover:text-[#E87B3A] transition-colors" data-testid="footer-admin">{t("nav.admin")}</Link></li>
          </ul>
        </div>

        {/* CONTACT */}
        <div className="col-span-6 md:col-span-4">
          <div className="chapter-label text-[#E87B3A]">{t("footer.contact")}</div>
          <ul className="mt-6 space-y-4 font-body text-sm">
            <li className="flex items-start gap-3">
              <Phone size={15} className="mt-1 text-[#E87B3A] shrink-0" />
              <a href={`tel:${phoneRaw}`} className="hover:text-[#E87B3A] transition-colors" data-testid="footer-phone">
                {contactName} — {phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle size={15} className="mt-1 text-[#25D366] shrink-0" />
              <a href={waHref} target="_blank" rel="noopener noreferrer" className="hover:text-[#25D366] transition-colors" data-testid="footer-wa">
                WhatsApp · {phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={15} className="mt-1 text-[#E87B3A] shrink-0" />
              <a href={`mailto:${email}`} className="hover:text-[#E87B3A] transition-colors" data-testid="footer-email">
                {email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={15} className="mt-1 text-[#E87B3A] shrink-0" />
              <span>{address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={15} className="mt-1 text-[#E87B3A] shrink-0" />
              <span style={{ whiteSpace: "pre-line" }}>{hours}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* HUGE brand mark */}
      <div className="relative border-t border-white/10 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mx-auto max-w-[1440px] px-6 lg:px-12 py-10 md:py-16 leading-none"
        >
          <div className="font-display uppercase text-white text-[18vw] md:text-[14vw] tracking-tight leading-[0.8]">
            LOCATION <span className="text-[#E87B3A]">RNC</span>
          </div>
        </motion.div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] font-body font-bold uppercase tracking-[0.3em] text-white/50">
          <p>© {new Date().getFullYear()} Location RNC. {t("footer.rights")}</p>
          <p>Remorques & Outillage · Québec · N 46°22′</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
